import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import {
  FaWandMagicSparkles,
  FaComments,
  FaMusic,
  FaGift,
  FaHeart,
  FaLock,
  FaClock,
  FaCircleInfo
} from 'react-icons/fa6';

const sampleSong = {
  title: 'A Song For Maya',
  description: 'A sample personalized song created from a mother’s memory of her daughter’s laugh.',
  duration: '3:12',
  url: null,
};

const PROCESS_STEPS = [
  {
    icon: FaComments,
    title: 'Share Your Story',
    description: 'Tell us about the person, memory, or occasion you want to celebrate.',
  },
  {
    icon: FaMusic,
    title: 'We Craft Your Song',
    description: 'Our songwriters and composers turn your story into an original, meaningful song.',
  },
  {
    icon: FaGift,
    title: 'Receive & Share',
    description: 'Get your personalized song to keep, gift, or share with the people you love.',
  },
];

const PROMPT_SUGGESTIONS = [
  { label: 'Birthday', text: 'Write a joyful birthday song celebrating ' },
  { label: 'Anniversary', text: 'Write a heartfelt anniversary song about ' },
  { label: 'Love Story', text: 'Tell the story of how we met and fell in love, including ' },
  { label: 'Healing', text: 'Write a gentle, comforting song about healing from ' },
  { label: 'Remembrance', text: 'Write a tender song in memory of ' },
  { label: 'Encouragement', text: 'Write an uplifting song of encouragement for someone facing ' },
];

const CreateSongPage = () => {
  const { user } = useAuth();
  const [promptText, setPromptText] = useState('');

  if (!user) {
    return (
      <div className="create-song-page">
        <div className="container">
          <div className="access-gate">
            <div className="access-gate-icon"><FaLock /></div>
            <div className="access-gate-title">Login Required</div>
            <div className="access-gate-text">
              Sign in or create an account to start a personalized song.
            </div>
            <div className="access-gate-actions">
              <Link to="/login" className="btn btn-primary">Login</Link>
              <Link to="/signup" className="btn btn-outline">Sign Up</Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const handleApplySuggestion = (suggestion) => {
    setPromptText((prev) => (prev ? `${prev}\n\n${suggestion.text}` : suggestion.text));
  };

  return (
    <div className="create-song-page">
      <div className="container">
        <div className="create-song-header">
          <div className="create-song-header-icon"><FaWandMagicSparkles /></div>
          <h1>Create a Personalized Song</h1>
          <p>
            Share a personal story, memory, or feeling — we'll transform it into a
            meaningful, one-of-a-kind song made just for you.
          </p>
        </div>

        <section className="create-song-section">
          <h2>Hear a Sample</h2>
          <div className="sample-song-card">
            <div className="sample-song-info">
              <h3>{sampleSong.title}</h3>
              <p>{sampleSong.description}</p>
              <span className="media-duration"><FaClock size={11} /> {sampleSong.duration}</span>
            </div>
            {sampleSong.url ? (
              <audio controls src={sampleSong.url} />
            ) : (
              <div className="media-coming-soon">
                <FaCircleInfo /> Sample coming soon
              </div>
            )}
          </div>
        </section>

        <Link
          to="/order-song"
          state={{ prompt: promptText }}
          className="btn btn-primary btn-large order-song-cta"
        >
          <FaHeart /> Order a Personalized Song
        </Link>

        <section className="create-song-section">
          <h2>How It Works</h2>
          <div className="process-steps">
            {PROCESS_STEPS.map((step, idx) => {
              const Icon = step.icon;
              return (
                <div key={step.title} className="process-step">
                  <div className="process-step-icon"><Icon /></div>
                  <div>
                    <h3>{idx + 1}. {step.title}</h3>
                    <p>{step.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        <section className="create-song-section">
          <h2>AI Prompts</h2>
          <p className="section-description">
            Not sure where to start? Describe the song, story, emotion, or occasion
            you're imagining below, or click a suggestion to explore an idea.
          </p>

          <div className="prompt-suggestions">
            {PROMPT_SUGGESTIONS.map((suggestion) => (
              <button
                key={suggestion.label}
                type="button"
                className="prompt-chip"
                onClick={() => handleApplySuggestion(suggestion)}
              >
                {suggestion.label}
              </button>
            ))}
          </div>

          <textarea
            className="form-input prompt-textarea"
            rows={5}
            placeholder="Describe the song, story, emotion, or occasion you're imagining..."
            value={promptText}
            onChange={(e) => setPromptText(e.target.value)}
          />
          <p className="prompt-hint">
            This space is just for exploring your ideas — it won't submit an order or
            generate a song. Use "Order a Personalized Song" above when you're ready.
          </p>
        </section>
      </div>
    </div>
  );
};

export default CreateSongPage;
